import { buildCapitalGlobalStepDadosDoSeguroAttachPayload } from '@/features/proposals/components/capital-global/utils/build-capital-global-step-dados-do-seguro-attach-payload.util';
import {
  capitalGlobalStep1SubStipulatorValidateFieldNames,
  capitalGlobalStep1ValidateFieldNames,
} from '@/features/proposals/components/capital-global/utils/capital-global-step1-validate-field-names.util';
import { clearCapitalGlobalCorrespondenceFields } from '@/features/proposals/components/capital-global/utils/clear-capital-global-correspondence-fields.util';
import {
  isCapitalGlobalLegalPerson,
  normalizeCnpjDocument,
} from '@/features/proposals/components/capital-global/utils/is-capital-global-legal-person.util';
import {
  resolveCapitalGlobalDocumentLabel,
  resolveCapitalGlobalIdTypeFromStoredCustomer,
} from '@/features/quotes/utils/resolve-capital-global-id-type.util';
import {
  capitalGlobalEffectiveStartDateWindowMessage,
  capitalGlobalMaxEffectiveStartDate,
  capitalGlobalMinEffectiveStartDate,
  isAfterCapitalGlobalEffectiveWindow,
} from '@/features/proposals/components/capital-global/utils/capital-global-effective-date-window.util';
import { mapProposalToCapitalGlobalStepDadosDoSeguroPrefill } from '@/features/proposals/components/capital-global/utils/map-proposal-to-step-dados-do-seguro-prefill.util';
import {
  isSubStipulantAddressIncomplete,
  mapProposalCustomersToSubStipulantAddresses,
  mergeSubStipulantAddressesDraft,
} from '@/features/proposals/components/capital-global/utils/map-proposal-customers-to-sub-stipulant-addresses.util';
import { capitalGlobalSubStipulatorAddressFieldNames } from '@/features/proposals/components/capital-global/constants/capital-global-proposal-address-field-names.constant';
import {
  applyCnpjAddressToAddressFields,
  applyCnpjAddressToRegistrationFields,
  isRegistrationAddressIncomplete,
  needsZipcodeStreetFallbackAfterCnpj,
} from '@/features/proposals/components/capital-global/utils/map-cnpj-address-to-registration-fields.util';
import {
  capitalGlobalStep1ShouldShowFieldErrors,
  type CapitalGlobalStep1FieldApiLike,
} from '@/features/proposals/components/capital-global/utils/capital-global-step1-should-show-field-errors.util';
import type { CapitalGlobalProposalStep1FormApi } from '@/features/proposals/components/capital-global/steps/capital-global-proposal-step1-form.types';
import type { CapitalGlobalProposalStepProps } from '@/features/proposals/components/capital-global/capital-global-proposal-step.types';
import { useUpdateProposal } from '@/features/proposals/hooks/use-update-proposal.hook';
import { cnpjService } from '@/services/cnpj';
import { useFetchZipcode } from '@/shared/hooks/use-fetch-zipcode.hook';
import { toaster } from '@/shared/ui/toaster.component';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useRef, useState } from 'react';

function calculateTerminateDateFromEffectiveDate(value: string) {
  const effectiveDate = DateTime.fromISO(value);
  if (!effectiveDate.isValid) return '';
  return effectiveDate.plus({ years: 1 }).toISODate() || '';
}

function isAllowedEffectiveStartDate(value: string) {
  const effectiveDate = DateTime.fromISO(value);
  const minDate = DateTime.fromISO(capitalGlobalMinEffectiveStartDate());
  return effectiveDate.isValid && effectiveDate >= minDate;
}

type Args = CapitalGlobalProposalStepProps & {
  form: CapitalGlobalProposalStep1FormApi;
};

export function useCapitalGlobalProposalStep1ViewModel({
  form,
  proposal,
  policyId,
  isViewMode,
  onNext,
  step1Draft,
  onStep1DraftChange,
}: Args) {
  const { mutateAsync: updateProposal, isPending: isUpdatingProposal } =
    useUpdateProposal(policyId, proposal?.productCode);
  const { mutateAsync: fetchZipcode, isPending: isLoadingRegCep } =
    useFetchZipcode('billing');
  const { mutateAsync: fetchCorrZipcode, isPending: isLoadingCorrCep } =
    useFetchZipcode('billing');
  const { mutateAsync: fetchSubZipcode } = useFetchZipcode('billing');

  const zipcodeLookupCacheRef = useRef<Record<string, boolean>>({});
  const submitAttemptedRef = useRef(false);
  const prefilledRef = useRef(false);
  const addressResolvedRef = useRef(false);
  const subAddressesResolvedRef = useRef(false);
  const [prefillDone, setPrefillDone] = useState(false);
  const [isLoadingCnpj, setIsLoadingCnpj] = useState(false);
  const [hasResolvedRegCep, setHasResolvedRegCep] = useState(false);
  const [isGenericRegCep, setIsGenericRegCep] = useState(false);
  const [hasResolvedCorrCep, setHasResolvedCorrCep] = useState(false);
  const [isGenericCorrCep, setIsGenericCorrCep] = useState(false);
  // Estado do CEP por subestipulante — mesmo par (resolvido / genérico) do estipulante,
  // indexado pela posição no array do form.
  const [subCepFlags, setSubCepFlags] = useState<
    { hasResolved: boolean; isGeneric: boolean }[]
  >([]);
  const [loadingSubCepIndexes, setLoadingSubCepIndexes] = useState<number[]>([]);
  /** Subs que reprovaram na última tentativa de "Avançar" — abrem o accordion. */
  const [subStipulatorErrorIndexes, setSubStipulatorErrorIndexes] = useState<
    number[]
  >([]);

  const shouldShowStep1FieldErrors = useCallback(
    (fieldApi: CapitalGlobalStep1FieldApiLike) => {
      return capitalGlobalStep1ShouldShowFieldErrors(
        isViewMode,
        submitAttemptedRef.current,
        fieldApi
      );
    },
    [isViewMode]
  );

  useEffect(() => {
    const clean = String(form.state.values.registrationCep || '').replace(
      /\D/g,
      ''
    );
    if (clean.length !== 8) {
      if (hasResolvedRegCep) setHasResolvedRegCep(false);
      if (isGenericRegCep) setIsGenericRegCep(false);
      return;
    }
    if (!hasResolvedRegCep) setHasResolvedRegCep(true);
    const gen = clean.endsWith('000');
    if (gen !== isGenericRegCep) setIsGenericRegCep(gen);
  }, [form.state.values.registrationCep, hasResolvedRegCep, isGenericRegCep]);

  useEffect(() => {
    const clean = String(form.state.values.correspondenceCep || '').replace(
      /\D/g,
      ''
    );
    if (clean.length !== 8) {
      if (hasResolvedCorrCep) setHasResolvedCorrCep(false);
      if (isGenericCorrCep) setIsGenericCorrCep(false);
      return;
    }
    if (!hasResolvedCorrCep) setHasResolvedCorrCep(true);
    const gen = clean.endsWith('000');
    if (gen !== isGenericCorrCep) setIsGenericCorrCep(gen);
  }, [
    form.state.values.correspondenceCep,
    hasResolvedCorrCep,
    isGenericCorrCep,
  ]);

  useEffect(() => {
    if (!proposal || prefilledRef.current) return;
    prefilledRef.current = true;

    const proposalPrefill =
      mapProposalToCapitalGlobalStepDadosDoSeguroPrefill(proposal);
    const rest = step1Draft ?? proposalPrefill;
    const registrationCepDigits = rest.registrationCep.replace(/\D/g, '');
    const correspondenceCepDigits = rest.correspondenceCep.replace(/\D/g, '');

    form.setFieldValue('customerPk', rest.customerPk);
    form.setFieldValue('cnpj', rest.cnpj);
    form.setFieldValue('companyName', rest.companyName);
    // CAEPF: sexo e data de nascimento do titular. Sem estas duas linhas o Step 1 perdia
    // o que já tinha sido preenchido ao avançar e voltar — o prefill os calcula, mas
    // ninguém os escrevia no form.
    form.setFieldValue('gender', rest.gender);
    form.setFieldValue('dateOfBirth', rest.dateOfBirth);
    form.setFieldValue(
      'effectiveStartDate',
      isAllowedEffectiveStartDate(rest.effectiveStartDate)
        ? rest.effectiveStartDate
        : ''
    );
    form.setFieldValue('registrationCep', rest.registrationCep);
    form.setFieldValue('registrationStreet', rest.registrationStreet);
    form.setFieldValue('registrationNumber', rest.registrationNumber);
    form.setFieldValue('registrationComplement', rest.registrationComplement);
    form.setFieldValue(
      'registrationNeighborhood',
      rest.registrationNeighborhood
    );
    form.setFieldValue('registrationCity', rest.registrationCity);
    form.setFieldValue('registrationState', rest.registrationState);
    form.setFieldValue(
      'sameCorrespondenceAsRegistration',
      rest.sameCorrespondenceAsRegistration
    );
    form.setFieldValue('correspondenceCep', rest.correspondenceCep);
    form.setFieldValue('correspondenceStreet', rest.correspondenceStreet);
    form.setFieldValue('correspondenceNumber', rest.correspondenceNumber);
    form.setFieldValue(
      'correspondenceComplement',
      rest.correspondenceComplement
    );
    form.setFieldValue(
      'correspondenceNeighborhood',
      rest.correspondenceNeighborhood
    );
    form.setFieldValue('correspondenceCity', rest.correspondenceCity);
    form.setFieldValue('correspondenceState', rest.correspondenceState);
    form.setFieldValue('stipulatorPhone', rest.stipulatorPhone);
    form.setFieldValue('stipulatorEmail', rest.stipulatorEmail);

    // Subestipulantes: identidade sempre da proposta, endereço do rascunho quando houver.
    // A lista tem tamanho fixo aqui (subs entram/saem na Cotação), então um único
    // `setFieldValue` no array basta — sem `mode='array'`.
    const subRows = mergeSubStipulantAddressesDraft(
      mapProposalCustomersToSubStipulantAddresses(proposal),
      step1Draft?.subStipulatorAddresses
    );
    form.setFieldValue('subStipulatorAddresses', subRows);
    setSubCepFlags(
      subRows.map(row => {
        const digits = row.cep.replace(/\D/g, '');
        return {
          hasResolved: digits.length === 8,
          isGeneric: digits.length === 8 && digits.endsWith('000'),
        };
      })
    );

    if (registrationCepDigits.length === 8) {
      setHasResolvedRegCep(true);
      setIsGenericRegCep(registrationCepDigits.endsWith('000'));
    }

    if (
      !rest.sameCorrespondenceAsRegistration &&
      correspondenceCepDigits.length === 8
    ) {
      setHasResolvedCorrCep(true);
      setIsGenericCorrCep(correspondenceCepDigits.endsWith('000'));
    }

    setPrefillDone(true);
  }, [form, proposal, step1Draft]);

  const fetchAndFillReg = useCallback(
    async (rawCep: string, options?: { onlyEmpty?: boolean }) => {
      const cleanCep = rawCep.replace(/\D/g, '');
      if (cleanCep.length !== 8) return;

      const onlyEmpty = options?.onlyEmpty ?? false;
      const values = form.state.values;

      try {
        const result = await fetchZipcode(cleanCep);
        if (result.success && result.data && !('error' in result.data)) {
          const d = result.data;
          zipcodeLookupCacheRef.current[cleanCep] = true;
          const isGeneric = cleanCep.endsWith('000');
          setHasResolvedRegCep(true);
          setIsGenericRegCep(isGeneric);

          if (!onlyEmpty || !values.registrationStreet) {
            form.setFieldValue('registrationStreet', d.street || '');
          }
          if (!onlyEmpty || !values.registrationNeighborhood) {
            form.setFieldValue(
              'registrationNeighborhood',
              d.neighborhood || ''
            );
          }
          if (!onlyEmpty || !values.registrationCity) {
            form.setFieldValue('registrationCity', d.city || '');
          }
          if (!onlyEmpty || !values.registrationState) {
            form.setFieldValue('registrationState', d.state || '');
          }
        }
      } catch {
        zipcodeLookupCacheRef.current[cleanCep] = false;

        if (!onlyEmpty) {
          setHasResolvedRegCep(false);
          setIsGenericRegCep(false);
          form.setFieldValue('registrationStreet', '');
          form.setFieldValue('registrationNeighborhood', '');
          form.setFieldValue('registrationCity', '');
          form.setFieldValue('registrationState', '');
        }

        toaster.create({
          type: 'error',
          title: 'Erro ao buscar CEP',
          description: 'Verifique o CEP informado e tente novamente.',
        });
      }
    },
    [fetchZipcode, form]
  );

  const fetchAndFillCorr = useCallback(
    async (rawCep: string, options?: { onlyEmpty?: boolean }) => {
      const cleanCep = rawCep.replace(/\D/g, '');
      if (cleanCep.length !== 8) return;

      const onlyEmpty = options?.onlyEmpty ?? false;
      const values = form.state.values;

      try {
        const result = await fetchCorrZipcode(cleanCep);
        if (result.success && result.data && !('error' in result.data)) {
          const d = result.data;
          zipcodeLookupCacheRef.current[cleanCep] = true;
          const isGeneric = cleanCep.endsWith('000');
          setHasResolvedCorrCep(true);
          setIsGenericCorrCep(isGeneric);

          if (!onlyEmpty || !values.correspondenceStreet) {
            form.setFieldValue('correspondenceStreet', d.street || '');
          }
          if (!onlyEmpty || !values.correspondenceNeighborhood) {
            form.setFieldValue(
              'correspondenceNeighborhood',
              d.neighborhood || ''
            );
          }
          if (!onlyEmpty || !values.correspondenceCity) {
            form.setFieldValue('correspondenceCity', d.city || '');
          }
          if (!onlyEmpty || !values.correspondenceState) {
            form.setFieldValue('correspondenceState', d.state || '');
          }
        }
      } catch {
        zipcodeLookupCacheRef.current[cleanCep] = false;

        if (!onlyEmpty) {
          setHasResolvedCorrCep(false);
          setIsGenericCorrCep(false);
          form.setFieldValue('correspondenceStreet', '');
          form.setFieldValue('correspondenceNeighborhood', '');
          form.setFieldValue('correspondenceCity', '');
          form.setFieldValue('correspondenceState', '');
        }

        toaster.create({
          type: 'error',
          title: 'Erro ao buscar CEP',
          description: 'Verifique o CEP informado e tente novamente.',
        });
      }
    },
    [fetchCorrZipcode, form]
  );

  const fetchAndFillSubStipulator = useCallback(
    async (index: number, rawCep: string, options?: { onlyEmpty?: boolean }) => {
      const cleanCep = rawCep.replace(/\D/g, '');
      if (cleanCep.length !== 8) return;

      const onlyEmpty = options?.onlyEmpty ?? false;
      const fields = capitalGlobalSubStipulatorAddressFieldNames(index);
      const current = form.state.values.subStipulatorAddresses[index];

      const setFlags = (hasResolved: boolean, isGeneric: boolean) =>
        setSubCepFlags(previous => {
          const next = [...previous];
          next[index] = { hasResolved, isGeneric };
          return next;
        });

      setLoadingSubCepIndexes(previous =>
        previous.includes(index) ? previous : [...previous, index]
      );

      try {
        const result = await fetchSubZipcode(cleanCep);
        if (result.success && result.data && !('error' in result.data)) {
          const d = result.data;
          zipcodeLookupCacheRef.current[cleanCep] = true;
          setFlags(true, cleanCep.endsWith('000'));

          if (!onlyEmpty || !current?.street) {
            form.setFieldValue(fields.street as never, (d.street || '') as never);
          }
          if (!onlyEmpty || !current?.neighborhood) {
            form.setFieldValue(
              fields.neighborhood as never,
              (d.neighborhood || '') as never
            );
          }
          if (!onlyEmpty || !current?.city) {
            form.setFieldValue(fields.city as never, (d.city || '') as never);
          }
          if (!onlyEmpty || !current?.state) {
            form.setFieldValue(fields.state as never, (d.state || '') as never);
          }
        }
      } catch {
        zipcodeLookupCacheRef.current[cleanCep] = false;

        if (!onlyEmpty) {
          setFlags(false, false);
          form.setFieldValue(fields.street as never, '' as never);
          form.setFieldValue(fields.neighborhood as never, '' as never);
          form.setFieldValue(fields.city as never, '' as never);
          form.setFieldValue(fields.state as never, '' as never);
        }

        toaster.create({
          type: 'error',
          title: 'Erro ao buscar CEP',
          description: 'Verifique o CEP informado e tente novamente.',
        });
      } finally {
        setLoadingSubCepIndexes(previous => previous.filter(i => i !== index));
      }
    },
    [fetchSubZipcode, form]
  );

  useEffect(() => {
    if (
      !prefillDone ||
      isViewMode ||
      !proposal ||
      addressResolvedRef.current
    ) {
      return;
    }

    addressResolvedRef.current = true;

    const resolveRegistrationAddress = async () => {
      const values = form.state.values;
      const documentNumber = values.cnpj || '';
      const isLegalPerson = isCapitalGlobalLegalPerson(proposal, documentNumber);

      if (isLegalPerson) {
        const cleanCnpj = normalizeCnpjDocument(documentNumber);
        if (cleanCnpj.length !== 14) return;

        setIsLoadingCnpj(true);
        try {
          const result = await cnpjService.getByCnpj(cleanCnpj);
          if (result.success && result.data?.address) {
            applyCnpjAddressToRegistrationFields(
              form,
              result.data.address,
              { onlyEmpty: true }
            );

            const cepDigits = String(
              form.state.values.registrationCep || ''
            ).replace(/\D/g, '');
            if (cepDigits.length === 8) {
              setHasResolvedRegCep(true);
              setIsGenericRegCep(cepDigits.endsWith('000'));
            }

            if (needsZipcodeStreetFallbackAfterCnpj(form.state.values)) {
              await fetchAndFillReg(form.state.values.registrationCep, {
                onlyEmpty: true,
              });
            }
            return;
          }

          toaster.create({
            type: 'error',
            title: 'Erro ao buscar CNPJ',
            description:
              result.error ||
              result.message ||
              'Não foi possível obter o endereço do estipulante.',
          });
        } catch {
          toaster.create({
            type: 'error',
            title: 'Erro ao buscar CNPJ',
            description:
              'Não foi possível obter o endereço do estipulante. Tente novamente.',
          });
        } finally {
          setIsLoadingCnpj(false);
        }
        return;
      }

      const cepDigits = String(values.registrationCep || '').replace(/\D/g, '');
      if (cepDigits.length !== 8 || !isRegistrationAddressIncomplete(values)) {
        return;
      }

      await fetchAndFillReg(values.registrationCep, { onlyEmpty: true });
    };

    void resolveRegistrationAddress();
  }, [prefillDone, isViewMode, proposal, form, fetchAndFillReg]);

  // Endereço dos subestipulantes — MESMA resolução do estipulante: consulta de CNPJ e,
  // quando ela vem sem logradouro, o CEP completa. Só age em linha ainda incompleta,
  // então o que voltou salvo do InsureMO nunca é sobrescrito.
  useEffect(() => {
    if (
      !prefillDone ||
      isViewMode ||
      !proposal ||
      subAddressesResolvedRef.current ||
      form.state.values.subStipulatorAddresses.length === 0
    ) {
      return;
    }

    subAddressesResolvedRef.current = true;

    const resolveSubStipulatorAddresses = async () => {
      const total = form.state.values.subStipulatorAddresses.length;

      // Sequencial de propósito: a Brasil API tem rate-limit estreito (~3 req/min por
      // IP) e uma apólice pode ter vários subs. O back cacheia 24h em sucesso.
      for (let index = 0; index < total; index++) {
        const entry = form.state.values.subStipulatorAddresses[index];
        if (!entry || !isSubStipulantAddressIncomplete(entry)) continue;

        const fields = capitalGlobalSubStipulatorAddressFieldNames(index);
        const cleanDocument = normalizeCnpjDocument(entry.documentNumber);

        if (entry.isCompany && cleanDocument.length === 14) {
          setLoadingSubCepIndexes(previous =>
            previous.includes(index) ? previous : [...previous, index]
          );
          try {
            const result = await cnpjService.getByCnpj(cleanDocument);
            if (result.success && result.data?.address) {
              applyCnpjAddressToAddressFields(form, fields, result.data.address, {
                onlyEmpty: true,
              });

              const cepDigits = String(
                form.state.values.subStipulatorAddresses[index]?.cep || ''
              ).replace(/\D/g, '');
              if (cepDigits.length === 8) {
                setSubCepFlags(previous => {
                  const next = [...previous];
                  next[index] = {
                    hasResolved: true,
                    isGeneric: cepDigits.endsWith('000'),
                  };
                  return next;
                });
              }
            }
          } catch {
            // Falha de consulta não bloqueia: o corretor ainda digita o CEP à mão.
            // Sem toaster — com vários subs viraria uma pilha de alertas.
          } finally {
            setLoadingSubCepIndexes(previous =>
              previous.filter(i => i !== index)
            );
          }
        }

        // CNPJ costuma trazer CEP sem logradouro — o CEP completa a rua.
        const current = form.state.values.subStipulatorAddresses[index];
        const cepDigits = String(current?.cep || '').replace(/\D/g, '');
        if (cepDigits.length === 8 && !String(current?.street || '').trim()) {
          await fetchAndFillSubStipulator(index, current.cep, {
            onlyEmpty: true,
          });
        }
      }
    };

    void resolveSubStipulatorAddresses();
  }, [prefillDone, isViewMode, proposal, form, fetchAndFillSubStipulator]);

  const minDate = capitalGlobalMinEffectiveStartDate();
  const maxDate = capitalGlobalMaxEffectiveStartDate();

  const canEditRegStreetNeighborhood = hasResolvedRegCep && isGenericRegCep;
  const canEditCorrStreetNeighborhood = hasResolvedCorrCep && isGenericCorrCep;

  const subStipulatorCepState = subCepFlags.map((flags, index) => ({
    isLoading: loadingSubCepIndexes.includes(index),
    hasResolved: flags.hasResolved,
    // Mesma regra do estipulante: logradouro/bairro só liberam em CEP genérico.
    canEditStreetNeighborhood: flags.hasResolved && flags.isGeneric,
  }));

  const onSameCorrespondenceChange = (checked: boolean) => {
    form.setFieldValue('sameCorrespondenceAsRegistration', checked);
    if (checked) {
      clearCapitalGlobalCorrespondenceFields(form);
      setHasResolvedCorrCep(false);
      setIsGenericCorrCep(false);
      return;
    }

    clearCapitalGlobalCorrespondenceFields(form);
    setHasResolvedCorrCep(false);
    setIsGenericCorrCep(false);
  };

  // Rótulo/máscara do documento do estipulante conforme o tipo (CNPJ/CAEPF/CPF).
  // Sem isto, CAEPF aparecia como "CNPJ" mascarado errado (e antes, o CPF derivado).
  // Fica ANTES do `handleSubmit` porque o "Próximo" também depende do tipo para saber
  // se Sexo/Data de Nascimento são obrigatórios.
  const estipulanteIdType = resolveCapitalGlobalIdTypeFromStoredCustomer({
    documentType: proposal?.customer?.documentType,
    documentNumber:
      (proposal?.customer as { documentNumber?: string })?.documentNumber ||
      proposal?.customer?.cpf ||
      '',
    isOrgParty: proposal?.customer?.isOrgParty,
  });
  const documentLabel = resolveCapitalGlobalDocumentLabel(estipulanteIdType);
  const documentMask: 'cnpj' | 'cpf' | 'caepf' =
    estipulanteIdType === '2' ? 'cnpj' : estipulanteIdType === '3' ? 'caepf' : 'cpf';

  const handleSubmit = async () => {
    if (isViewMode) {
      onNext?.();
      return;
    }

    submitAttemptedRef.current = true;

    // Propostas antigas podem chegar com vigência fora da janela de 30 dias; o
    // erro inline só aparece se o campo tiver sido tocado, então avisamos aqui.
    if (
      isAfterCapitalGlobalEffectiveWindow(form.state.values.effectiveStartDate)
    ) {
      toaster.create({
        type: 'error',
        title: 'Vigência fora do prazo permitido',
        description: capitalGlobalEffectiveStartDateWindowMessage(),
      });
      return;
    }

    const fieldNamesToValidate = capitalGlobalStep1ValidateFieldNames({
      sameCorrespondenceAsRegistration:
        form.state.values.sameCorrespondenceAsRegistration,
      documentMask,
    });

    const subFieldNamesByIndex =
      capitalGlobalStep1SubStipulatorValidateFieldNames(
        form.state.values.subStipulatorAddresses
      );

    const hasFieldError = (results: unknown[]) =>
      results.some(r => Array.isArray(r) && r.length > 0);

    const [baseResults, subResults] = await Promise.all([
      Promise.all(
        fieldNamesToValidate.map(name =>
          form.validateField(name as never, 'change')
        )
      ),
      Promise.all(
        subFieldNamesByIndex.map(names =>
          Promise.all(
            names.map(name => form.validateField(name as never, 'change'))
          )
        )
      ),
    ]);

    const failedSubIndexes = subResults.flatMap((results, index) =>
      hasFieldError(results) ? [index] : []
    );
    setSubStipulatorErrorIndexes(failedSubIndexes);

    if (hasFieldError(baseResults) || failedSubIndexes.length > 0) return;

    const v = form.state.values;
    const payload = buildCapitalGlobalStepDadosDoSeguroAttachPayload(v, {
      terminateDate: calculateTerminateDateFromEffectiveDate(
        v.effectiveStartDate
      ),
      proponentName: proposal?.customer?.name || '',
    });

    try {
      onStep1DraftChange?.({ ...v });
      await updateProposal(payload);
      onNext?.();
    } catch (error) {
      toaster.create({
        type: 'error',
        title: 'Não foi possível salvar',
        description: error instanceof Error ? error.message : 'Erro inesperado',
      });
    }
  };

  return {
    minDate,
    maxDate,
    documentLabel,
    documentMask,
    isLoadingRegCep: isLoadingRegCep || isLoadingCnpj,
    isLoadingCorrCep,
    hasResolvedRegCep,
    hasResolvedCorrCep,
    canEditRegStreetNeighborhood,
    canEditCorrStreetNeighborhood,
    zipcodeLookupCacheRef,
    shouldShowStep1FieldErrors,
    fetchAndFillReg,
    fetchAndFillCorr,
    onSameCorrespondenceChange,
    subStipulatorCepState,
    subStipulatorErrorIndexes,
    fetchAndFillSubStipulator,
    handleSubmit,
    isUpdatingProposal,
  };
}
